import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  DcButton,
  DcDropDown,
  DcInput,
  DcOverlaySlider,
  PixelFactoryContext,
  PixelTable,
} from 'pixel-base'
import { AjaxResponse } from 'rxjs/ajax'
import {
  getAllJournalEntries,
  getJournalEntry,
  getJournals,
} from 'utils/api-routes-accounts'
import AddNewEntry from './components/AddNewEntry'
import JournalEntryDetails from './components/JournalEntryDetails'
import FinancePageShell from '../../components/FinancePageShell'
import FinanceStatusBadge from '../../components/FinanceStatusBadge'

const FILTER_KEYS = [
  'journal_id',
  'status',
  'document_date_from',
  'document_date_to',
  'posting_date_from',
  'posting_date_to',
  'reference',
  'move_number',
] as const

const JournalEntries: React.FC = () => {
  const { showFailure, showPageLoader, hidePageLoader } = useContext(
    PixelFactoryContext,
  ) as any
  const [searchParams, setSearchParams] = useSearchParams()
  const [data, setData] = useState<any[]>([])
  const [showAddNew, setShowAddNew] = useState(false)
  const [isShowSpinner, setIsShowSpinner] = useState(false)
  const [selectedEntry, setSelectedEntry] = useState<any | null>(null)
  const [isLoadingEntry, setIsLoadingEntry] = useState(false)
  const [journals, setJournals] = useState<any[]>([])

  const filterValues = useMemo(
    () => ({
      journal_id: searchParams.get('journal_id') || '',
      status: searchParams.get('status') || '',
      document_date_from: searchParams.get('document_date_from') || '',
      document_date_to: searchParams.get('document_date_to') || '',
      posting_date_from: searchParams.get('posting_date_from') || '',
      posting_date_to: searchParams.get('posting_date_to') || '',
      reference: searchParams.get('reference') || '',
      move_number: searchParams.get('move_number') || '',
    }),
    [searchParams],
  )

  const effectiveFilters = useMemo(
    () => ({
      journal_id: filterValues.journal_id || undefined,
      status: filterValues.status || undefined,
      document_date_from: filterValues.document_date_from || undefined,
      document_date_to: filterValues.document_date_to || undefined,
      posting_date_from: filterValues.posting_date_from || undefined,
      posting_date_to: filterValues.posting_date_to || undefined,
      reference: filterValues.reference || undefined,
      move_number: filterValues.move_number || undefined,
    }),
    [filterValues],
  )

  const updateFilter = useCallback(
    (key: (typeof FILTER_KEYS)[number], value: string) => {
      const nextParams = new URLSearchParams(searchParams)
      if (value) {
        nextParams.set(key, value)
      } else {
        nextParams.delete(key)
      }
      setSearchParams(nextParams, { replace: true })
    },
    [searchParams, setSearchParams],
  )

  const resetFilters = useCallback(() => {
    setSearchParams(new URLSearchParams(), { replace: true })
  }, [setSearchParams])

  const fetchData = useCallback(
    (activeFilters = effectiveFilters) => {
      showPageLoader?.()
      setIsShowSpinner(true)
      getAllJournalEntries(activeFilters).subscribe(
        (res) => {
          setData((res as AjaxResponse<any>).response?.data || [])
          hidePageLoader?.()
          setIsShowSpinner(false)
        },
        (error) => {
          hidePageLoader?.()
          setIsShowSpinner(false)
          showFailure(error?.message || 'Failed to fetch journal entries')
        },
      )
    },
    [effectiveFilters, hidePageLoader, showFailure, showPageLoader],
  )

  useEffect(() => {
    fetchData(effectiveFilters)
  }, [effectiveFilters, fetchData])

  useEffect(() => {
    const subscription = getJournals().subscribe({
      next: (res) => setJournals(res.response?.data || []),
      error: () => showFailure('Failed to fetch journals'),
    })

    return () => subscription.unsubscribe()
  }, [showFailure])

  const handleRowClick = (row) => {
    setIsLoadingEntry(true)
    setSelectedEntry(row)
    getJournalEntry(row.id).subscribe(
      (res) => {
        setSelectedEntry((res as AjaxResponse<any>).response?.data || row)
        setIsLoadingEntry(false)
      },
      () => {
        setIsLoadingEntry(false)
        showFailure('Failed to fetch journal entry details')
      },
    )
  }

  return (
    <>
      <FinancePageShell
        title="Ledger Entries"
        breadcrumbs={[
          { name: 'Finance', active: false },
          { name: 'Journal Entries', active: true },
        ]}
        isLoading={isShowSpinner || isLoadingEntry}
        primaryAction={{ label: 'Add New', onClick: () => setShowAddNew(true) }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1.2fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr auto',
            gap: 12,
            marginBottom: 16,
          }}
        >
          <DcDropDown
            value={filterValues.journal_id}
            options={[
              { value: '', label: 'All Journals' },
              ...journals.map((journal) => ({
                value: String(journal.id),
                label: journal.name,
              })),
            ]}
            onChange={(e) => updateFilter('journal_id', e.target.value)}
          />
          <DcDropDown
            value={filterValues.status}
            options={[
              { value: '', label: 'All Statuses' },
              { value: 'draft', label: 'Draft' },
              { value: 'posted', label: 'Posted' },
              { value: 'cancelled', label: 'Cancelled' },
              { value: 'reversed', label: 'Reversed' },
            ]}
            onChange={(e) => updateFilter('status', e.target.value)}
          />
          <DcInput
            type="date"
            value={filterValues.document_date_from}
            onChange={(e) => updateFilter('document_date_from', e.target.value)}
          />
          <DcInput
            type="date"
            value={filterValues.document_date_to}
            onChange={(e) => updateFilter('document_date_to', e.target.value)}
          />
          <DcInput
            type="date"
            value={filterValues.posting_date_from}
            onChange={(e) => updateFilter('posting_date_from', e.target.value)}
          />
          <DcInput
            type="date"
            value={filterValues.posting_date_to}
            onChange={(e) => updateFilter('posting_date_to', e.target.value)}
          />
          <DcInput
            placeholder="Reference"
            value={filterValues.reference}
            onChange={(e) => updateFilter('reference', e.target.value)}
          />
          <DcInput
            placeholder="Move Number"
            value={filterValues.move_number}
            onChange={(e) => updateFilter('move_number', e.target.value)}
          />
          <DcButton
            type="button"
            variant="outline-secondary"
            onClick={resetFilters}
          >
            Reset
          </DcButton>
        </div>
        <PixelTable
          columns={[
            {
              name: 'Document Date',
              selector: (row) =>
                row.document_date || row.date?.date
                  ? new Date(
                      row.document_date || row.date?.date,
                    ).toLocaleDateString()
                  : '--',
            },
            {
              name: 'Posting Date',
              selector: (row) =>
                row.posting_date
                  ? new Date(row.posting_date).toLocaleDateString()
                  : '--',
            },
            {
              name: 'Journal',
              selector: (row) =>
                row.journal
                  ? `${row.journal.code || row.journal.short_code || '--'} - ${
                      row.journal.name
                    }`
                  : '--',
            },
            {
              name: 'Number',
              selector: (row) => row.move_number || row.name || '--',
            },
            { name: 'Reference', selector: (row) => row.reference || '--' },
            {
              name: 'Currency',
              selector: (row) => row.currency_code || row.currency || 'PKR',
            },
            {
              name: 'Amount',
              selector: (row) =>
                row.amount_total
                  ? Number(row.amount_total).toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                    })
                  : '--',
            },
            {
              name: 'Status',
              selector: (row) => (
                <FinanceStatusBadge status={row.status || row.state} />
              ),
            },
          ]}
          data={data}
          pagination
          searchable
          sortable
          fixedHeader
          fixedHeaderScrollHeight="calc(100vh - 300px)"
          onRowClicked={handleRowClick}
        />
      </FinancePageShell>

      <DcOverlaySlider
        isOpen={showAddNew}
        onClose={() => setShowAddNew(false)}
        title="Add New Journal Entry"
        width="45%"
      >
        <AddNewEntry
          handleClose={(_response, action) => {
            setShowAddNew(false)
            if (action === 'add') {
              fetchData(effectiveFilters)
            }
          }}
        />
      </DcOverlaySlider>

      <DcOverlaySlider
        isOpen={Boolean(selectedEntry)}
        onClose={() => setSelectedEntry(null)}
        title="Journal Entry Details"
        width="45%"
      >
        {selectedEntry && (
          <JournalEntryDetails
            entry={selectedEntry}
            isLoading={isLoadingEntry}
            onClose={() => setSelectedEntry(null)}
            onRefresh={() => {
              fetchData(effectiveFilters)
              getJournalEntry(selectedEntry.id).subscribe((res) => {
                setSelectedEntry(
                  (res as AjaxResponse<any>).response?.data || selectedEntry,
                )
              })
            }}
          />
        )}
      </DcOverlaySlider>
    </>
  )
}

export default JournalEntries
